import { Banknote } from 'lucide-react';
import { InfoCard, InfoRow } from '@/Components/shared/InfoCard';
import { useContractRefunds } from '@/features/contracts/hooks/useContracts';
import type { ContractRefund } from '@/features/contracts/types/contract.types';
import {
  COMMISSION_STATUS_LABELS,
  formatCents,
  REFUND_STATUS_LABELS,
} from '@/features/contracts/utils/refundLabels';

interface ContractRefundsCardProps {
  contractId: number;
}

const STATUS_TONE: Record<ContractRefund['status'], string> = {
  waiting_payment: 'tw-bg-amber-50 tw-text-amber-700',
  waiting_funds: 'tw-bg-amber-50 tw-text-amber-700',
  creating: 'tw-bg-blue-50 tw-text-blue-700',
  pending: 'tw-bg-blue-50 tw-text-blue-700',
  succeeded: 'tw-bg-green-50 tw-text-green-700',
  failed: 'tw-bg-red-50 tw-text-red-700',
  canceled: 'tw-bg-gray-100 tw-text-gray-600',
};

const REASON_LABELS: Record<ContractRefund['reason'], string> = {
  cancellation: "À l'annulation du contrat",
  first_charge_postponed: 'Prélèvements décalés à la date de début',
};

/**
 * Remboursement client demandé à l'annulation du contrat, ou au report de son 1er
 * prélèvement (contrat qui continue), et commission Affinicar.
 * N'affiche rien tant qu'aucun remboursement n'a été demandé.
 */
export function ContractRefundsCard({ contractId }: ContractRefundsCardProps) {
  const { data: refunds } = useContractRefunds(contractId);

  if (!refunds || refunds.length === 0) return null;

  return (
    <InfoCard title="Remboursement client" icon={<Banknote className="tw-w-4 tw-h-4" />}>
      <div className="tw-space-y-4">
        {refunds.map((refund) => (
          <div key={refund.id} className="tw-space-y-1">
            <span
              className={`tw-inline-block tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-rounded-full ${STATUS_TONE[refund.status]}`}
            >
              {REFUND_STATUS_LABELS[refund.status]}
            </span>
            <InfoRow label="Motif" value={REASON_LABELS[refund.reason] ?? REASON_LABELS.cancellation} />
            <InfoRow label="Montant" value={formatCents(refund.amountCents)} />
            <InfoRow
              label="Paiement remboursé"
              value={`du ${new Date(refund.paymentDate).toLocaleDateString('fr-FR')}${
                refund.paymentsCount > 1 ? ` (dernier de ${refund.paymentsCount} paiements)` : ''
              }`}
            />
            <InfoRow label="Demandé le" value={new Date(refund.requestedAt).toLocaleDateString('fr-FR')} />
            <InfoRow
              label="Commission Affinicar"
              value={`${formatCents(refund.commissionCents)} — ${COMMISSION_STATUS_LABELS[refund.commissionStatus]}`}
              hide={!refund.commissionCents}
            />
            <InfoRow
              label="Détail"
              value={refund.failureReason ?? ''}
              hide={!refund.failureReason || refund.status === 'succeeded'}
            />
          </div>
        ))}
      </div>
    </InfoCard>
  );
}
